import LayersClearOutlined from "@mui/icons-material/LayersClearOutlined";
import SwapHorizOutlined from "@mui/icons-material/SwapHorizOutlined";
import { Box, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { type FC, useMemo, useState } from "react";
import { ALPHA, DIFF_REVIEW, FONT_SIZE, RADIUS } from "../../../../theme/tokens";
import MetaChip, { type MetaChipTone } from "../../Badges/MetaChip";
import { CollapsibleSection, CollapsibleSectionVariant } from "../../Panels/CollapsibleSection";
import {
	countRecordStates,
	type DiffRecord,
	type PairedRecordDiff,
	pairRecordSets,
	type RecordDiffMember,
	type RecordDiffState,
	type RecordMemberChange,
} from "./recordSetDiffModel";

export type { DiffRecord, RecordDiffMember, RecordDiffState } from "./recordSetDiffModel";

/**
 * Every string this component renders. Nothing is derived from a value here — the caller owns the
 * vocabulary, the same contract `ValueDiffPair` keeps, so the component stays usable for rules,
 * mapping rows or revisions without knowing what any of them are.
 */
export type RecordSetDiffLabels = {
	/** The headline fact, always shown: e.g. "Accepting replaces the whole list". */
	replaceTitle: string;
	/**
	 * One sentence naming what is destroyed, rendered ONLY when something is actually removed — so the
	 * warning means something on the rows where it appears instead of firing on every row.
	 */
	destructionBody: string;
	/** The word for each state, used for both the summary strip and each record's badge. */
	state: Record<RecordDiffState, string>;
	/** Caption for the before/after value of a changed member (read out, shown on hover). */
	currentCaption: string;
	incomingCaption: string;
	/** The collapsed remainder of a paired record, e.g. `(count) => t("…", { count })`. */
	unchangedMembersLabel: (count: number) => string;
	/** Stand-in for a member that exists on only one side of a paired record. */
	absentValue: string;
	/** Accessible name of the record list. */
	listAriaLabel: string;
};

type Props = {
	/** The records the destination holds now. Empty is legal — everything then reads as added. */
	currentRecords: DiffRecord[];
	/** The records that would replace them. */
	incomingRecords: DiffRecord[];
	labels: RecordSetDiffLabels;
	/** Cap the list's height; past it the records scroll, leaving the replace notice in view. */
	maxHeight?: number;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

const STATE_TONE: Record<RecordDiffState, MetaChipTone> = { removed: "error", changed: "warning", added: "success", unchanged: "neutral" };

/**
 * A sign per state, so the four states are separable without colour — the same guarantee
 * `ValueDiffPair` makes with its leading `−`/`+`.
 */
const STATE_SIGN: Record<RecordDiffState, string> = { removed: "−", changed: "~", added: "+", unchanged: "=" };

/** The order the summary strip counts them in — destruction first, mirroring the list below it. */
const SUMMARY_ORDER: RecordDiffState[] = ["removed", "changed", "added", "unchanged"];

/** Resolve a state's rail/ink pair off the theme, so the whole thing re-tints with the palette. */
function useStateAccent(): (state: RecordDiffState) => { rail: string; ink: string } {
	const theme = useTheme();
	return (state) => {
		if (state === "removed") return { rail: theme.palette.error.main, ink: theme.palette.error.dark };
		if (state === "added") return { rail: theme.palette.success.main, ink: theme.palette.success.dark };
		if (state === "changed") return { rail: theme.palette.warning.main, ink: theme.palette.warning.dark };
		return { rail: theme.palette.divider, ink: theme.palette.text.secondary };
	};
}

/** One value as it reads on its side: struck error ink when it is going, success ink when it arrives. */
const ValuePill: FC<{ value: string; tone: "removed" | "added" | "neutral"; caption?: string }> = ({ value, tone, caption }) => (
	<Box
		component="span"
		sx={(theme) => {
			if (tone === "neutral") return { fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", wordBreak: "break-word" };
			const accent = tone === "removed" ? theme.palette.error : theme.palette.success;
			return {
				fontSize: FONT_SIZE.SECONDARY,
				fontWeight: 600,
				lineHeight: 1.45,
				px: 0.5,
				borderRadius: `${RADIUS.DIFF_LINE}px`,
				wordBreak: "break-word",
				backgroundColor: alpha(accent.main, ALPHA.DIFF_LINE_TINT),
				color: accent.dark,
				// Not colour-alone: the value being replaced is struck as well as tinted.
				...(tone === "removed" ? { textDecoration: "line-through" } : {}),
			};
		}}
	>
		{caption && <Box component="span" sx={visuallyHidden}>{`${caption}: `}</Box>}
		{value}
	</Box>
);

/**
 * A flat run of `label value` pairs. Toned for a record that exists on one side only — all of it is
 * moving, so all of it is marked — and NEUTRAL for the collapsed remainder of a paired record, which is
 * there to be checked rather than to be noticed.
 */
const MemberSummary: FC<{ members: RecordDiffMember[]; tone: "removed" | "added" | "neutral"; dataTestId?: string }> = ({ members, tone, dataTestId }) => (
	<Box data-testid={dataTestId} sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.25, minWidth: 0 }}>
		{members.map((member) => (
			<Box key={member.key} sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}>
				<Box component="span" sx={{ fontSize: FONT_SIZE.LABEL_XS, color: "text.secondary", flexShrink: 0 }}>
					{member.label}
				</Box>
				<ValuePill value={member.value} tone={tone} />
			</Box>
		))}
	</Box>
);

/** One member that moved: what it was, an arrow, what it becomes. The signal this component is for. */
const MemberChangeRow: FC<{ change: RecordMemberChange; labels: RecordSetDiffLabels; dataTestId?: string }> = ({ change, labels, dataTestId }) => (
	<Box
		data-testid={dataTestId}
		sx={{
			display: "grid",
			// The label column is its own track so several changed members line their arrows up.
			// `fit-content` rather than a fixed width: it shrinks to the longest member name actually
			// present — a lone `breakLimit` should not leave a hand's width of gap before its values —
			// and only caps at the token when a name is long enough to crowd them out. One column where
			// there is no room for two.
			gridTemplateColumns: { xs: "1fr", sm: `fit-content(${DIFF_REVIEW.MEMBER_LABEL_WIDTH}px) 1fr` },
			columnGap: 1,
			rowGap: 0.25,
			alignItems: "baseline",
			minWidth: 0,
		}}
	>
		<Box component="span" sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 600, color: "text.secondary", wordBreak: "break-word" }}>
			{change.label}
		</Box>
		<Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
			{change.currentValue === null ? (
				<ValuePill value={labels.absentValue} tone="neutral" caption={labels.currentCaption} />
			) : (
				<ValuePill value={change.currentValue} tone="removed" caption={labels.currentCaption} />
			)}
			<Box component="span" aria-hidden sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.disabled", flexShrink: 0 }}>
				→
			</Box>
			{change.incomingValue === null ? (
				<ValuePill value={labels.absentValue} tone="neutral" caption={labels.incomingCaption} />
			) : (
				<ValuePill value={change.incomingValue} tone="added" caption={labels.incomingCaption} />
			)}
		</Box>
	</Box>
);

/** One record and what became of it. Owns its own disclosure — the caller never wires per-record state. */
const RecordCard: FC<{ pair: PairedRecordDiff; labels: RecordSetDiffLabels; dataTestId?: string }> = ({ pair, labels, dataTestId }) => {
	const [showUnchanged, setShowUnchanged] = useState(false);
	const accentFor = useStateAccent();
	const accent = accentFor(pair.state);
	const isRemoved = pair.state === "removed";

	return (
		<Box
			component="li"
			data-testid={dataTestId}
			data-state={pair.state}
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				gap: 0.5,
				p: 0.75,
				pl: 1,
				borderRadius: `${RADIUS.CARD}px`,
				borderLeft: `${DIFF_REVIEW.RECORD_RAIL}px solid ${accent.rail}`,
				backgroundColor: alpha(pair.state === "unchanged" ? theme.palette.text.primary : accent.rail, ALPHA.DIFF_PANEL_TINT),
				minWidth: 0,
			})}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
				<Box component="span" aria-hidden sx={{ fontFamily: "monospace", fontSize: FONT_SIZE.SECONDARY, color: accent.ink, flexShrink: 0 }}>
					{STATE_SIGN[pair.state]}
				</Box>
				<Typography
					component="span"
					sx={{
						fontSize: FONT_SIZE.EMPHASIS,
						fontWeight: 600,
						color: pair.state === "unchanged" ? "text.secondary" : "text.primary",
						lineHeight: 1.3,
						wordBreak: "break-word",
						minWidth: 0,
						// A rule that is going reads as gone in the title too, not only in its values.
						...(isRemoved ? { textDecoration: "line-through", opacity: ALPHA.MUTED_SECONDARY } : {}),
					}}
				>
					{pair.title}
				</Typography>
				<MetaChip label={labels.state[pair.state]} tone={STATE_TONE[pair.state]} size="small" sx={{ ml: "auto", flexShrink: 0 }} />
			</Box>

			{pair.changedMembers.map((change) => (
				<MemberChangeRow key={change.key} change={change} labels={labels} dataTestId={dataTestId ? `${dataTestId}-member-${change.key}` : undefined} />
			))}

			{pair.members.length > 0 && (
				<MemberSummary members={pair.members} tone={isRemoved ? "removed" : "added"} dataTestId={dataTestId ? `${dataTestId}-members` : undefined} />
			)}

			{pair.unchangedMembers.length > 0 && (
				<CollapsibleSection
					variant={CollapsibleSectionVariant.Inline}
					title={labels.unchangedMembersLabel(pair.unchangedMembers.length)}
					open={showUnchanged}
					onToggle={() => setShowUnchanged((previous) => !previous)}
					dataTestId={dataTestId ? `${dataTestId}-toggle` : undefined}
				>
					<MemberSummary members={pair.unchangedMembers} tone="neutral" dataTestId={dataTestId ? `${dataTestId}-unchanged` : undefined} />
				</CollapsibleSection>
			)}
		</Box>
	);
};

/**
 * **Two lists of structured records, and what changed between them** — which records are going, which
 * are new, and for a record on both sides, which single member actually moved.
 *
 * The problem it solves: a field whose value is a LIST OF OBJECTS (a department's break rules, its
 * overtime rules, a batch of HRIS mapping rows) is replaced wholesale by an import or a bulk edit. Laid
 * out as two flat lists of `key: value · key: value` lines — which is what `ValueDiffPair` does, and is
 * right for a scalar or a list of strings — a 12-member rule is already a long line, and one threshold
 * moving is indistinguishable from the whole set being rewritten. So this pairs the two sides by a
 * caller-chosen key, shows removed / added / changed as distinct states, and for a paired record
 * renders ONLY the members that differ, collapsing the identical remainder behind a disclosure.
 *
 * The replace notice above the list is deliberate and not decoration: the hazard of this shape of field
 * is a reviewer approving it believing it MERGES. The notice states the replacement every time, and
 * names the destruction only when records are actually being removed, so the warning still means
 * something where it appears.
 *
 * Purely presentational and prop-driven — the caller formats each record into `{matchKey, title,
 * members}` and owns every string, so it stays generic over any "review this change before applying it"
 * surface. Reach for `ValueDiffPair` instead when the value is a scalar or a list of plain strings;
 * that is the line-shaped comparison, this is the record-shaped one. Inside a `SystemDataGrid` cell,
 * pair with `dataGridProps.getRowHeight: () => "auto"` for the same reason `ValueDiffPair` documents.
 */
export const RecordSetDiff: FC<Props> = ({ currentRecords, incomingRecords, labels, maxHeight, dataTestId, sx }) => {
	const pairs = useMemo(() => pairRecordSets(currentRecords, incomingRecords), [currentRecords, incomingRecords]);
	const counts = useMemo(() => countRecordStates(pairs), [pairs]);
	const isDestructive = counts.removed > 0;

	return (
		<Box
			data-testid={dataTestId}
			sx={[{ display: "flex", flexDirection: "column", gap: 0.75, width: "100%", minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
		>
			<Box
				data-testid={dataTestId ? `${dataTestId}-notice` : undefined}
				sx={(theme) => {
					// Two planes, not one: the WASH is the same ink either way so the notice always reads as a
					// band, while the RAIL is the volume knob — error where rules are being deleted, the same
					// divider an unchanged card wears where the replacement loses nothing.
					const wash = isDestructive ? theme.palette.error.main : theme.palette.text.primary;
					const rail = isDestructive ? theme.palette.error.main : theme.palette.divider;
					return {
						display: "flex",
						flexWrap: "wrap",
						alignItems: "center",
						gap: 0.75,
						p: 0.75,
						pl: 1,
						borderRadius: `${RADIUS.CARD}px`,
						borderLeft: `${DIFF_REVIEW.NOTICE_RAIL}px solid ${rail}`,
						backgroundColor: alpha(wash, ALPHA.DIFF_PANEL_TINT),
					};
				}}
			>
				<Box
					component={isDestructive ? LayersClearOutlined : SwapHorizOutlined}
					aria-hidden
					sx={{ fontSize: FONT_SIZE.TITLE_SM, color: isDestructive ? "error.main" : "text.secondary", flexShrink: 0 }}
				/>
				<Typography
					component="span"
					sx={{
						fontSize: FONT_SIZE.LABEL_XS,
						fontWeight: 700,
						letterSpacing: "0.06em",
						textTransform: "uppercase",
						color: isDestructive ? "error.dark" : "text.secondary",
					}}
				>
					{labels.replaceTitle}
				</Typography>
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, ml: "auto" }}>
					{SUMMARY_ORDER.filter((state) => counts[state] > 0).map((state) => (
						<MetaChip
							key={state}
							label={`${counts[state]} ${labels.state[state]}`}
							tone={STATE_TONE[state]}
							size="small"
							dataTestId={dataTestId ? `${dataTestId}-summary-${state}` : undefined}
						/>
					))}
				</Box>
				{isDestructive && (
					<Typography
						component="span"
						data-testid={dataTestId ? `${dataTestId}-destruction` : undefined}
						sx={{ flexBasis: "100%", fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: "error.dark", lineHeight: 1.4 }}
					>
						{labels.destructionBody}
					</Typography>
				)}
			</Box>

			<Box
				component="ul"
				aria-label={labels.listAriaLabel}
				data-testid={dataTestId ? `${dataTestId}-list` : undefined}
				sx={{
					listStyle: "none",
					m: 0,
					p: 0,
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					minWidth: 0,
					...(maxHeight ? { maxHeight, overflowY: "auto" } : {}),
				}}
			>
				{pairs.map((pair, index) => (
					<RecordCard key={pair.key} pair={pair} labels={labels} dataTestId={dataTestId ? `${dataTestId}-record-${index}` : undefined} />
				))}
			</Box>
		</Box>
	);
};
